import { useCallback } from 'react'

import { PositionClaimFormValues } from '~/components/smart-accounts/positions/positionClaimSchema'
import { PositionBalance } from '~/components/smart-accounts/positions/types'
import {
  buildPositionClaimData,
  getPositionClaimTitle,
} from '~/components/smart-accounts/positions/utils'
import { CreateTransactionData } from '~/hooks/smart-account-actions/createTransactionData.type'
import { useCreateTransaction } from '~/hooks/smart-account-actions/useCreateTransaction'
import {
  BackendLoggingData,
  BasicTransactionType,
  OrgTransaction,
} from '~/types'

type CreateClaimTransactionParams = {
  claimForm: PositionClaimFormValues
  positionBalance: PositionBalance
  loggingData?: BackendLoggingData
}

export const useCreatePositionClaimTransaction = (): {
  createClaimTransaction: (
    data: CreateClaimTransactionParams,
    dontOpenDialog: boolean,
    onSuccess?: (transaction: OrgTransaction) => void,
  ) => Promise<void>
  isProcessing: boolean
} => {
  const { createTransaction, isProcessing } = useCreateTransaction()

  const createClaimTransaction = useCallback(
    async (
      data: CreateClaimTransactionParams,
      dontOpenDialog: boolean,
      onSuccess?: (transaction: OrgTransaction) => void,
    ) => {
      const name = getPositionClaimTitle(data.positionBalance)

      const createTransactionData: CreateTransactionData = {
        chainId: data.claimForm.chainId,
        name,
        smartAccountId: data.positionBalance.smartAccountId,
        smartAccountAddress: data.claimForm.account,
        calls: data.claimForm.calls,
        isOfframp: false,
        onSuccess,
        memo: data.claimForm.memo,
        types: [BasicTransactionType.Claim],
        transactionData: {
          positionClaimData: buildPositionClaimData({
            positionBalance: data.positionBalance,
            chainId: data.claimForm.chainId,
          }),
        },
      }

      createTransaction({
        transactionId: undefined,
        txData: createTransactionData,
        dontOpenDialog,
        loggingData: data.loggingData,
      })
    },
    [createTransaction],
  )

  return {
    createClaimTransaction,
    isProcessing,
  }
}
